import React from 'react'
import { FormattedMessage } from 'react-intl'
import { css } from '@emotion/css'
import gql from 'graphql-tag'
import { Mutation } from 'react-apollo'
import { CellRenderer } from '../../legacy-design-system/grid'
import { ServerEnvironment, environmentI18nConfig } from './shared'
import { LoadingTextMask } from '../../legacy-design-system/loading-masks'
import { EditableTextDropdown } from '../../legacy-design-system/editable-text'
import { Role, RoleSwitch } from '../roles'
import { PureQueryOptions } from 'apollo-client'
import { I18nConsumer } from '../i18n-provider'
import { allServerEnvironments } from '../server-environment'

interface GetRendererProps {
  readonly organizationRole: Role
  readonly organizationID: string
  readonly refetchQueries: PureQueryOptions[]
  readonly refresh: () => Promise<any>
}

interface RowData {
  readonly id: string
  readonly environment: ServerEnvironment
}

const dropdownContainerCss = css`
  position: absolute;
  top: calc(50% - 18px);
`

const readonlyTextCss = css`
  padding: 0 8px;
`

const setEnvironmentMutation = gql`
  mutation ChangeServerEnvironment(
    $organizationID: ID!
    $id: ID!
    $environment: Environment!
  ) {
    changeServerEnvironment(
      input: {
        organizationID: $organizationID
        id: $id
        environment: $environment
      }
    ) {
      id
      environment
    }
  }
`

export function getEnvironmentCellRenderer({
  organizationID,
  organizationRole,
  refetchQueries,
  refresh
}: GetRendererProps): CellRenderer<RowData> {
  return function environmentCellRenderer({
    rowData: { id: serverID, environment },
    getCellContentProps
  }) {
    return (
      <div {...getCellContentProps({})}>
        <RoleSwitch
          role={organizationRole}
          requiredRole="ADMIN"
          renderAllowed={() => {
            return (
              <Mutation mutation={setEnvironmentMutation}>
                {(setEnvironment) => (
                  <I18nConsumer>
                    {({ intl }) => {
                      return (
                        <div className={dropdownContainerCss}>
                          <EditableTextDropdown
                            styleOverrides={{
                              selectionContainerCss: css`
                                padding-left: 8px;
                                padding-right: 8px;
                                &:not(:hover) {
                                  background-image: none;
                                  border-color: transparent;
                                }
                              `
                            }}
                            items={allServerEnvironments}
                            selectedItem={environment}
                            itemToString={(env) => {
                              return intl.formatMessage({
                                id: environmentI18nConfig[env]
                              })
                            }}
                            onChange={(newEnvironment) =>
                              setEnvironment({
                                variables: {
                                  organizationID,
                                  id: serverID,
                                  environment: newEnvironment
                                },
                                refetchQueries,
                                optimisticResponse: {
                                  changeServerEnvironment: {
                                    id: serverID,
                                    environment: newEnvironment,
                                    __typename: 'Server'
                                  },
                                  __typename: 'Mutation'
                                }
                              }).then(refresh)
                            }
                          />
                        </div>
                      )
                    }}
                  </I18nConsumer>
                )}
              </Mutation>
            )
          }}
          renderDenied={() => {
            return (
              <FormattedMessage id={environmentI18nConfig[environment]}>
                {(text) => {
                  return <span className={readonlyTextCss}>{text}</span>
                }}
              </FormattedMessage>
            )
          }}
        />
      </div>
    )
  }
}

const loadingCellCss = css`
  padding: 8px;
  width: 100%;
`

export function loadingEnvironmentCell() {
  return (
    <span className={loadingCellCss}>
      <LoadingTextMask min={2} max={7} />
    </span>
  )
}
